import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eyebrow } from '../components/Eyebrow';
import { StatusChip } from '../components/StatusChip';
import { Button } from '../components/Button';
import { PropertyClassifierForm } from '../components/property';
import { getDraft, PropertyApiError } from '../lib/propertyApi';
import {
  saveAnswers,
  saveClassifier,
  saveDraftId,
} from '../lib/propertyStorage';

/**
 * Property Insurance Quote — entry screen (classifier + resume handler).
 *
 * - Default: renders the 8-question classifier.
 * - `?resume={token}`: verifies the token via GET /property/draft/{token},
 *   hydrates classifier + answers into sessionStorage, then navigates to
 *   /property/form with the draft's state intact.
 */
export function Property() {
  const [searchParams] = useSearchParams();
  const resumeToken = searchParams.get('resume');

  if (resumeToken) {
    return <ResumeHandler token={resumeToken} />;
  }

  return <Classifier />;
}

function Classifier() {
  return (
    <>
      <Navbar />
      <main className="container-site py-20">
        <div className="flex flex-col gap-10 max-w-3xl">
          <div className="flex flex-col gap-6">
            <StatusChip label="PROPERTY_INSURANCE · CLASSIFICATION" tone="active" />
            <h1 className="font-display font-medium tracking-[-0.02em] leading-[1.05] text-[clamp(36px,5.5vw,60px)]">
              Property insurance quote for large habitational buildings.
            </h1>
            <p className="max-w-prose text-ink-muted text-[1.0625rem] leading-relaxed">
              Eight quick questions to classify the risk. We only ask what's
              relevant to your building — answers here decide which sections
              you'll see next, and which carrier forms Derek prepares on
              submission.
            </p>
            <Eyebrow>TAKES · ABOUT_90_SECONDS</Eyebrow>
          </div>

          <div className="border-t border-hairline pt-10">
            <PropertyClassifierForm />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ResumeHandler({ token }: { token: string }) {
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const draft = await getDraft(token);
        if (cancelled) return;
        if (draft.classifier) saveClassifier(draft.classifier);
        if (draft.answers) saveAnswers(draft.answers);
        if (draft.draftId) saveDraftId(draft.draftId);
        navigate('/property/form', { replace: true });
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof PropertyApiError
            ? resumeErrorMessage(err.code)
            : err instanceof Error
              ? err.message
              : 'Could not load the saved draft.';
        setError(msg);
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  if (state === 'loading') {
    return (
      <>
        <Navbar />
        <main className="container-site py-20">
          <div className="flex flex-col gap-4 max-w-xl">
            <StatusChip label="PROPERTY_INSURANCE · RESUMING" tone="active" />
            <h1 className="font-display text-[clamp(28px,4vw,44px)] font-medium tracking-[-0.02em] leading-[1.1]">
              Picking up where you left off&hellip;
            </h1>
            <p className="text-ink-muted">Verifying your resume link and loading your draft.</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="container-site py-20">
        <div className="flex flex-col gap-6 max-w-xl">
          <StatusChip label="PROPERTY_INSURANCE · RESUME_FAILED" tone="error" />
          <h1 className="font-display text-[clamp(28px,4vw,44px)] font-medium tracking-[-0.02em] leading-[1.1]">
            That link didn't work.
          </h1>
          <p className="text-ink-muted">{error}</p>
          <div className="flex flex-wrap gap-3">
            <Button as="link" to="/property" variant="primary">
              Start a new quote
            </Button>
            <Button as="link" to="/" variant="ghost">
              Return home
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function resumeErrorMessage(code: string): string {
  switch (code) {
    case 'expired':
      return 'Your resume link has expired. Drafts last 30 days — start a new quote and we\'ll send a fresh link.';
    case 'invalid-signature':
    case 'malformed':
      return 'This resume link is invalid. Double-check the URL or start a new quote.';
    case 'draft_not_found':
      return "We couldn't find a draft for this link.";
    case 'draft_submitted':
      return 'This draft has already been submitted. Derek will follow up — no further action needed.';
    case 'draft_expired':
      return 'This draft has expired. Start a new quote to begin.';
    default:
      return 'Something went wrong loading your draft. Please try again.';
  }
}

export default Property;
